import { Text, Tooltip } from '@noorden-insurtech/alba-design-system';
import { AlertTriangle, DollarSign } from 'lucide-react';

export type InstallmentOption = {
  /** Rótulo do nº de parcelas: "À vista", "1+1", "1+2"... */
  label: string;
  /** "Sem Juros" ou o percentual formatado (ex.: "1,99%") */
  interestLabel: string;
  /** Valor da parcela já formatado em moeda */
  installmentValue: string;
  /** Total já formatado em moeda */
  total: string;
};

type PremiumCardProps = {
  value: string;
  title?: string;
  showAlert?: boolean;
  alertMessage?: string;
  installmentText?: string;
  installmentOptions?: InstallmentOption[];
};

const infoBadgeClass =
  'flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-bold text-[#18163D]';

const installmentColumnsClass =
  'grid grid-cols-[0.85fr_1.1fr_1fr_0.85fr] items-center gap-x-4';

function InstallmentOptionsTable({
  options,
}: {
  options: InstallmentOption[];
}) {
  return (
    <div className='w-[520px] max-w-[88vw] text-left'>
      <div className='mb-4 flex items-center gap-2'>
        <DollarSign
          className='h-5 w-5 text-alba-black-500'
          strokeWidth={2.5}
        />
        <span className='text-base font-bold text-alba-black-500'>
          Opções de Parcelamento
        </span>
      </div>

      <div className='rounded-2xl bg-alba-gray-50 p-3'>
        <div
          className={`${installmentColumnsClass} px-4 pb-3 text-xs font-semibold whitespace-nowrap text-alba-gray-500`}
        >
          <span>N de Parcelas</span>
          <span>Percentual de Juros</span>
          <span className='text-right'>Valor da Parcela</span>
          <span className='text-right'>Total</span>
        </div>

        <div className='overflow-hidden rounded-xl bg-white'>
          {options.map((option, index) => (
            <div
              key={option.label}
              className={`${installmentColumnsClass} px-4 py-3 text-sm ${
                index > 0 ? 'border-t border-alba-gray-100' : ''
              }`}
            >
              <span className='text-alba-black-500'>{option.label}</span>
              <span className='text-alba-gray-400'>{option.interestLabel}</span>
              <span className='text-right font-medium text-alba-black-500'>
                {option.installmentValue}
              </span>
              <span className='text-right text-alba-gray-400'>
                {option.total}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PremiumCard({
  value,
  title = 'Prêmio Total',
  showAlert = false,
  alertMessage = 'Condições Alteradas. Valor precisa ser recalculado!',
  installmentText,
  installmentOptions,
}: PremiumCardProps) {
  const hasInstallmentOptions = !!installmentOptions?.length;

  return (
    <div className='flex flex-col items-center w-full max-w-[704px] mx-auto rounded-2xl bg-gradient-to-br from-[#2C285F] to-[#18163D] px-6 py-6 text-center'>
      <Text className='text-base text-alba-white-500 mb-2'>{title}</Text>
      <Text
        className={`text-[34px] md:text-[40px] font-bold leading-[44px] text-alba-white-500 ${
          showAlert ? 'blur-[6px]' : ''
        }`}
      >
        {value}
      </Text>

      {installmentText ? (
        <div className='flex items-center justify-center gap-2 pt-3'>
          <Text className='text-sm font-semibold text-alba-green-500'>
            {installmentText}
          </Text>
          {hasInstallmentOptions ? (
            <Tooltip.Root
              openDelay={100}
              closeDelay={150}
              interactive
              theme='light'
              positioning={{ placement: 'bottom' }}
            >
              <Tooltip.Trigger asChild>
                <button
                  type='button'
                  aria-label='Ver opções de parcelamento'
                  className={`${infoBadgeClass} cursor-pointer`}
                >
                  i
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Positioner>
                  <Tooltip.Content className='max-w-none rounded-3xl border border-alba-gray-100 bg-white p-5 shadow-xl'>
                    <InstallmentOptionsTable options={installmentOptions} />
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Portal>
            </Tooltip.Root>
          ) : (
            <div className={infoBadgeClass}>i</div>
          )}
        </div>
      ) : showAlert ? (
        <div className='flex items-center justify-center gap-2 pt-4'>
          <AlertTriangle
            className='h-4 w-4 text-[#F46363] relative -top-[1px]'
            strokeWidth={1.5}
          />
          <Text className='text-sm text-[#F46363]'>{alertMessage}</Text>
        </div>
      ) : null}
    </div>
  );
}
